// ChatDock — sohbetin her sayfada açılabilen sağ yan paneli (design_handoff_sohbet_panel).
//
// Neden: kullanıcı teklif/izin/sipariş ekranından ÇIKMADAN mesaj okuyup yazabilsin.
// Bildirim gelince panel o kanalla açılır; isteyen "Tam ekran" ile /chat'e geçer.
//
// Panelde YALNIZ sohbet vardır — Belgeler, Super Agents, Kaydedilenler gibi bölümler
// tam /chat ekranında kalır (handoff kararı).
//
// Mesaj akışı ve composer /chat ile AYNI bileşenlerdir (MessageList / Composer):
// ikisi ayrışırsa panelde gönderilen mesaj tam ekranda farklı görünürdü.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Hash, X, Menu, Maximize2, Minimize2, ExternalLink, Lock, Star, MessageSquare, ArrowLeft } from 'lucide-react'
import { chatService } from '../../services/chatService'
import { useAppStore } from '../../stores/appStore'
import { useAuthStore } from '../../stores/authStore'
import { useChatDock, DOCK_W_MIN, DOCK_W_NARROW, DOCK_W_WIDE } from '../../stores/chatDockStore'
import { useChatStream } from '../../hooks/useChatStream'
import { toast } from '../../stores/uiStore'
import { dropOutgoingMessage, mergeOutgoing, showOutgoingMessage } from './outgoingMessage'
import { avatarGradient, initials } from '../crm/pipeline/tokens'
import MessageList from './MessageList'
import Composer from './Composer'
import ThreadPanel from './ThreadPanel'
import UserProfilePanel from './UserProfilePanel'
import { useChatMessageActions } from './useChatMessageActions'
import { jumpToMessage } from './messageJump'
import ThreadResizeHandle, { loadThreadWidth, saveThreadWidth, THREAD_W_DEFAULT } from './ThreadResizeHandle'
import DockResizeHandle from './DockResizeHandle'

export default function ChatDock() {
  const { open, listOpen, dockWidth, activeId, focusMessageId, focusThreadId,
          close, toggleList, toggleWide, setDockWidth, setActive, clearFocus } = useChatDock()
  const company = useAppStore((s) => s.activeCompany)
  const co = company && company !== '*' ? company : '01'
  const myUserId = useAuthStore((s) => s.user?.id)
  const qc = useQueryClient()
  const nav = useNavigate()
  const { pathname } = useLocation()
  // Konu (yanıtla): Teams gibi mesaj akışının YANINDA açılır/kapanır (X veya geri).
  // Kişi kartı dar alanda hâlâ akışın YERİNE açılır.
  const [threadId, setThreadId] = useState(null)
  const [profileId, setProfileId] = useState(null)
  const [threadW, setThreadW] = useState(() => loadThreadWidth(Math.min(THREAD_W_DEFAULT, 340)))
  const [dragging, setDragging] = useState(false)
  const [liveDockW, setLiveDockW] = useState(null) // sürüklerken min altı da görünsün (kapanış öncesi)
  const isWide = dockWidth >= (DOCK_W_NARROW + DOCK_W_WIDE) / 2
  const effectiveDockW = liveDockW ?? dockWidth

  // Kanal + DM listesi (belge kanalları panelde GÖSTERİLMEZ — handoff)
  const { data: convData } = useQuery({
    queryKey: ['chat-conversations', co],
    queryFn: () => chatService.listConversations(co),
    // Panel kapalıyken 60sn (rozet); açıkken 30sn — her 30sn'de sunucuyu yormasın
    refetchInterval: open ? 30000 : 60000,
    staleTime: 15000,
  })
  const channels = convData?.data?.channels || []
  const dms = convData?.data?.dms || []
  const all = useMemo(() => [...channels, ...dms], [channels, dms])

  // Açılışta kanal seçili değilse ilkini seç
  useEffect(() => {
    if (open && !activeId && all.length) setActive(all[0].id)
  }, [open, activeId, all, setActive])

  const active = useMemo(() => all.find((c) => c.id === activeId) || null, [all, activeId])

  // Bildirimden gelindiyse mesajı ÇEVRESİYLE yükle (around_id) — hedef mesaj eski
  // olabilir; yalnız son sayfa çekilirse vurgulanacak satır listede hiç olmaz.
  const { data: msgData, isLoading } = useQuery({
    queryKey: ['chat-messages', activeId, focusMessageId || 0],
    queryFn: () => chatService.listMessages(co, activeId,
      focusMessageId ? { around_id: focusMessageId } : {}),
    enabled: open && !!activeId,
  })
  const messages = useMemo(
    () => mergeOutgoing(activeId, msgData?.data?.messages || []),
    [activeId, msgData])

  const refreshConv = () => {
    qc.invalidateQueries({ queryKey: ['chat-conversations', co] })
    // Kanalı okumak kutudaki bildirimi de okundu yapar (sunucu: chat_service.
    // _read_channel_notifications) → gelen kutusu ve zil hemen tazelenmeli,
    // yoksa okunan satır 60 sn'lik yoklamaya kadar kutuda asılı kalır.
    qc.invalidateQueries({ queryKey: ['chat-inbox'] })
    qc.invalidateQueries({ queryKey: ['notifications'] })
    qc.invalidateQueries({ queryKey: ['notifications-unread-count'] })
  }
  const refreshMsgs = () => qc.invalidateQueries({ queryKey: ['chat-messages', activeId] })
  const err = (e) => toast.error('Hata', e.response?.data?.detail || e.message)

  const mSend = useMutation({
    mutationFn: async ({ payload, files }) => {
      const r = await chatService.sendMessage(co, activeId, payload)
      const mid = r?.data?.id
      if (mid && files?.length) {
        for (const f of files) {
          try { await chatService.uploadAttachment(co, mid, f) } catch (e) { err(e) }
        }
      }
      return r
    },
    onMutate: (v) => {
      clearFocus()
      const tempId = showOutgoingMessage(qc, { channelId: activeId, payload: v?.payload })
      return { tempId }
    },
    onSuccess: () => {
      refreshMsgs()
      refreshConv()
    },
    onError: (e, _v, ctx) => {
      dropOutgoingMessage(qc, { channelId: activeId, tempId: ctx?.tempId })
      err(e)
    },
  })
  const mReact = useMutation({
    mutationFn: ({ messageId, emoji }) => chatService.toggleReaction(co, messageId, emoji),
    onSuccess: refreshMsgs, onError: err,
  })
  const mRead = useMutation({ mutationFn: () => chatService.markRead(co, activeId), onSuccess: refreshConv })

  // Kanal açılınca / yeni mesaj gelince okundu işaretle (tam ekranla aynı davranış)
  const readRef = useRef(null)
  useEffect(() => {
    if (!open || !activeId || !messages.length) return
    const last = messages[messages.length - 1]?.id
    if (last && readRef.current !== `${activeId}:${last}`) {
      readRef.current = `${activeId}:${last}`
      mRead.mutate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeId, messages.length])

  // CANLI akış: panel de tam ekran gibi SSE dinler. Yoksa başkasının mesajı (ve konuya
  // gelen yanıtlar) panel açıkken görünmüyor, kullanıcı /chat'e girip çıkmak zorunda
  // kalıyordu (25.08.2026 bildirimi). Panel kapalıyken de zararsız: yalnız rozet tazelenir.
  // /chat açıkken ChatPage zaten dinliyor → panelde İKİNCİ bağlantı açma.
  // useLocation: sayfa değişince yeniden değerlendirilsin (window.location render'da donardı).
  const onChatPage = String(pathname || '').startsWith('/chat')
  useChatStream(activeId, threadId, open && !onChatPage)

  // Mesaj aksiyonları — tam ekranla ORTAK hook (İlet, Ata, Göreve bağla, Kaydet,
  // Düzenle, Sil, Kopyala…). Panelde bunlar hiç bağlı değildi: düğmeler görünüyor,
  // tıklanınca hiçbir şey olmuyordu (18.08.2026 kullanıcı bildirimi).
  // Alıntılı yanıt: /chat sayfasında vardı, yüzen pencerede YOKTU — aynı düğme
  // burada tıklanınca hiçbir şey olmuyordu (onReply prop'u hiç verilmemişti).
  const [replyTo, setReplyTo] = useState(null)
  const { onMessageAction, onDelete, closeAll: closeMsgActions, portals: actionPortals,
    editing: msgEditing, saveEdit: msgSaveEdit, cancelEdit: msgCancelEdit } = useChatMessageActions({
    company: co, channelId: activeId, refreshMsgs, refreshConv,
  })

  // Kanal değişince / panel kapanınca konu, kişi kartı ve açık modaller kapansın
  // Kanal/pencere değişince alıntı bandı da düşsün — başka kanalın mesajını
  // alıntılamış hâlde kalmak, yanlış kanala alıntı göndermeye yol açar.
  useEffect(() => { setThreadId(null); setProfileId(null); setReplyTo(null); closeMsgActions() }, [activeId])  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!open) { setThreadId(null); setProfileId(null); setReplyTo(null); closeMsgActions() } }, [open])  // eslint-disable-line react-hooks/exhaustive-deps

  // Bildirimden gelen odak: konu yanıtıysa konuyu aç. SIRA ÖNEMLİ — yukarıdaki
  // sıfırlama efektinden SONRA çalışmalı, yoksa açtığı konuyu kendisi kapatır.
  useEffect(() => { if (focusThreadId) setThreadId(focusThreadId) }, [focusThreadId])
  // Panel kapanınca odak düşsün (kullanıcı elle kanal seçince store zaten temizler)
  useEffect(() => { if (!open) clearFocus() }, [open, clearFocus])

  // around_id ile açılan eski mesaj vurgusu bittikten sonra odağı temizle —
  // aksi halde her refetch eski pencereyi çeker, yeni mesajlar listede görünmez
  // (kullanıcı /chat'e gidip başka mesaj atınca ancak görünüyordu).
  useEffect(() => {
    if (!focusMessageId || !messages.length) return undefined
    if (!messages.some((m) => String(m.id) === String(focusMessageId))) return undefined
    const t = setTimeout(() => clearFocus(), 2000)
    return () => clearTimeout(t)
  }, [focusMessageId, messages, clearFocus])

  const totalUnread = useMemo(
    () => all.reduce((t, c) => t + (c.unread_count || 0), 0), [all])

  // Panel KAPALI → sağ altta yüzen "Sohbet" düğmesi (design_handoff_sohbet_panel).
  // Üst bardaki düğmeyle aynı işi görür; tasarım isteği olarak ikisi birden durur.
  if (!open) {
    return (
      <button
        onClick={() => useChatDock.getState().toggle()}
        title="Sohbet panelini aç"
        // KATMAN: z-30 — header'ın (z-20) üstünde ama TÜM overlay'lerin altında
        // (modal z-40/50, izin·masraf paneli z-40/50, ürün talebi paneli z-70).
        // z-[80] iken sağ alt köşedeki "Onayla"/"Reddet" düğmelerinin üstüne biniyordu.
        className="hidden lg:inline-flex fixed bottom-6 right-6 z-30 items-center justify-center
                   w-[46px] h-[46px] rounded-full bg-primary-500 hover:bg-primary-600 text-white
                   shadow-[0_8px_24px_-6px_rgba(97,97,255,.55)] transition-colors"
      >
        <MessageSquare size={19} />
        {/* Okunmamış sayısı: yazı kalkınca hap daraldı → sayaç köşeye rozet olarak taşındı */}
        {totalUnread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[19px] h-[19px] px-1 rounded-full
                           bg-white text-primary-600 text-[10px] font-bold flex items-center
                           justify-center shadow ring-2 ring-primary-500">
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
      </button>
    )
  }

  // Konu açıkken kanal listesi gizlenir; kişi kartı açıkken akış yerine geçer
  const showList = listOpen && !threadId && !profileId
  // dockWidth: sürüklenen ana sütun; liste + konu paneli üstüne eklenir
  const width = effectiveDockW + (showList ? 160 : 0) + (threadId && !profileId ? threadW : 0)

  const goFull = () => {
    close()
    nav(activeId ? `/chat/c/${activeId}` : '/chat')
  }

  const closeThread = () => { setThreadId(null); clearFocus() }

  const onDockResize = (w) => {
    setDragging(true)
    // Min altındaysa henüz store'a yazma — kapanış eşiğine doğru canlı daralt
    if (w < DOCK_W_MIN) setLiveDockW(w)
    else { setDockWidth(w); setLiveDockW(null) }
  }
  const onDockCollapse = () => {
    setLiveDockW(null)
    setDragging(false)
    close()
  }
  const onDockDragEnd = () => {
    setLiveDockW(null)
    setDragging(false)
  }

  return (
    <aside
      className={`hidden lg:flex flex-none flex-row border-l border-gray-200 bg-white overflow-hidden animate-[dockin_.16s_ease-out] ${
        dragging ? '' : 'transition-[width] duration-200 ease-out'
      }`}
      style={{ width }}
    >
      {/* Sol kenar akordeon tutamacı — sürükle daralt/genişlet, çift tık / çok dar → kapat */}
      <DockResizeHandle
        width={effectiveDockW}
        onChange={onDockResize}
        onCollapse={onDockCollapse}
        onDragEnd={onDockDragEnd}
      />
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
      {/* Başlık — kanal listesi toggle, kanal adı + meta, aksiyonlar */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100 shrink-0">
        {profileId ? (
          <button onClick={() => setProfileId(null)} title="Sohbete dön"
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 shrink-0">
            <ArrowLeft size={15} />
          </button>
        ) : (
          <button onClick={toggleList} title={listOpen ? 'Kanal listesini gizle' : 'Kanal listesi'}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 shrink-0"
            disabled={!!threadId}>
            <Menu size={15} />
          </button>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            {active?.kind === 'dm'
              ? <span className={`w-4 h-4 rounded-full bg-gradient-to-br ${avatarGradient(active?.title || '')} shrink-0`} />
              : <Hash size={13} className="text-gray-400 shrink-0" />}
            <span className="text-[13px] font-semibold text-gray-900 truncate">{active?.title || 'Sohbet'}</span>
            {active?.is_private ? <Lock size={10} className="text-gray-400 shrink-0" /> : null}
          </div>
          {active?.member_count ? (
            <div className="text-[11px] text-gray-400 truncate">
              {active.member_count} üye{active.is_private ? ' · özel kanal' : ''}
            </div>
          ) : null}
        </div>
        <button onClick={goFull} title="Tam ekranda aç"
          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 shrink-0"><ExternalLink size={15} /></button>
        <button onClick={toggleWide} title={isWide ? 'Daralt' : 'Genişlet'}
          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 shrink-0">
          {isWide ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
        </button>
        <button onClick={close} title="Kapat"
          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 shrink-0"><X size={15} /></button>
      </div>

      <div className="flex-1 flex min-h-0">
        {/* Kanal listesi — yalnız kanal + DM (belge kanalları tam ekranda) */}
        {showList && (
          <div className="w-[160px] shrink-0 border-r border-gray-100 bg-[#FAFBFD] overflow-y-auto py-1.5">
            <Section title="Kanallar" />
            {channels.map((c) => <Row key={c.id} c={c} active={c.id === activeId} onClick={() => setActive(c.id)} />)}
            {channels.length === 0 && <Empty text="Kanal yok" />}
            <Section title="Direkt Mesajlar" />
            {dms.map((d) => <Row key={d.id} c={d} dm active={d.id === activeId} onClick={() => setActive(d.id)} />)}
            {dms.length === 0 && <Empty text="Sohbet yok" />}
          </div>
        )}

        {profileId ? (
          <UserProfilePanel company={co} userId={profileId} className="flex-1 min-w-0"
            onClose={() => setProfileId(null)}
            onStartDm={() => { setProfileId(null); goFull() }} />
        ) : (
          <>
            {/* Ana sohbet — konu açıkken de görünür (Teams yan panel) */}
            <div className="flex-1 min-w-0 flex flex-col">
              {activeId ? (
                <>
                  <MessageList
                    messages={messages} isLoading={isLoading} myUserId={myUserId} company={co}
                    channelKind={active?.kind} channelId={activeId}
                    onReact={(messageId, emoji) => mReact.mutate({ messageId, emoji })}
                    onOpenThread={(mid) => { setProfileId(null); setThreadId(mid) }}
                    onOpenProfile={(uid) => { setThreadId(null); setProfileId(uid) }}
                    onReply={setReplyTo} onQuoteJump={jumpToMessage}
                    onEdit={onMessageAction} onDelete={onDelete}
                    highlightId={focusMessageId}
                    hideActions={['ai']}
                  />
                  <Composer company={co} channelId={activeId} compact placeholder={`${active?.title || 'Sohbet'} kanalına yaz…`}
                    editing={msgEditing} onSaveEdit={msgSaveEdit} onCancelEdit={msgCancelEdit}
                    replyTo={replyTo} onCancelReply={() => setReplyTo(null)}
                    onSend={(payload, files) => {
                      const q = replyTo?.id
                      if (q) setReplyTo(null)
                      mSend.mutate({ payload: q ? { ...payload, quote_msg_id: q } : payload, files })
                    }} />
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-[12px] text-gray-400 px-4 text-center">
                  Soldan bir kanal seç
                </div>
              )}
            </div>

            {/* Konu paneli — açılır/kapanır, ayırıcı sürüklenir */}
            {threadId && activeId ? (
              <>
                <ThreadResizeHandle
                  width={threadW}
                  onChange={(w) => { setThreadW(w); saveThreadWidth(w) }}
                />
                <ThreadPanel company={co} rootId={threadId} channelId={activeId} channelName={active?.title}
                  channelKind={active?.kind} myUserId={myUserId} width={threadW} className="shrink-0 border-l border-gray-100"
                  onOpenProfile={(uid) => { setThreadId(null); setProfileId(uid) }}
                  onMessageAction={onMessageAction} onDeleteMessage={onDelete} hideActions={['ai']}
                  onClose={closeThread}
                  onChanged={() => { refreshMsgs(); refreshConv() }} />
              </>
            ) : null}
          </>
        )}
      </div>
      </div>

      {/* İlet / Ata / Göreve bağla / Düzenle modalleri — panelin üstünde açılır */}
      {actionPortals}
    </aside>
  )
}

function Section({ title }) {
  return <div className="px-2.5 pt-2.5 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{title}</div>
}

function Empty({ text }) {
  return <div className="px-2.5 py-1 text-[11px] text-gray-300">{text}</div>
}

function Row({ c, active, dm, onClick }) {
  const unread = c.unread_count || 0
  const nm = (c.title || '').trim() || (dm ? 'Kullanıcı' : 'Kanal')
  return (
    <button onClick={onClick}
      className={`w-full flex items-center gap-1.5 px-2.5 py-1.5 text-left ${
        active ? 'bg-violet-50 text-violet-700 shadow-[inset_2px_0_0_#5B4BE1]' : 'hover:bg-gray-50 text-gray-700'}`}>
      {dm
        ? <span className={`w-4 h-4 rounded-full bg-gradient-to-br ${avatarGradient(nm)} flex items-center justify-center text-[7px] font-semibold text-white shrink-0`}>{initials(nm)}</span>
        : <Hash size={12} className="text-gray-400 shrink-0" />}
      <span className={`text-[12px] truncate flex-1 ${unread ? 'font-semibold text-gray-900' : ''}`}>{nm}</span>
      {c.is_favorite ? <Star size={9} className="text-amber-400 fill-amber-400 shrink-0" /> : null}
      {unread > 0 && (
        <span className="shrink-0 min-w-[16px] h-[16px] px-1 rounded-full bg-violet-600 text-white text-[9px] font-semibold flex items-center justify-center">
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </button>
  )
}
